import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';

interface User {
  id: number;
  name: string;
  email: string;
  whatsapp?: string;
  verified?: boolean;
  email_verified?: boolean;
  preferred_language?: string;
  role?: string;
  created_at?: string;
  updated_at?: string;
}

interface UserAuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: { email: string; password: string }) => Promise<{
    message: string;
    user: any;
    token: string;
  }>;
  register: (userData: {
    name: string;
    email: string;
    whatsapp: string;
    password: string;
    language?: string;
  }) => Promise<{ requires_verification?: boolean }>;
  logout: () => void;
  verifyEmail: (email: string, otp: string) => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
}

const UserAuthContext = createContext<UserAuthContextType | undefined>(undefined);

interface UserAuthProviderProps {
  children: ReactNode;
}

export const UserAuthProvider: React.FC<UserAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = () => {
      const token = localStorage.getItem('authToken');
      const userStr = localStorage.getItem('user');
      
      if (token && userStr) {
        try {
          const userData = JSON.parse(userStr);
          setUser(userData);
        } catch (error) {
          // Error parsing user data
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials: { email: string; password: string }) => {
    setIsLoading(true);
    try {
      const response = await apiService.login(credentials);
      if (response.user && response.token) {
        setUser(response.user);
        return response;
      }
      throw new Error('Invalid response from server');
    } catch (error) {
      // Re-throw the error for the login component to handle
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: {
    name: string;
    email: string;
    whatsapp: string;
    password: string;
    language?: string;
  }) => {
    setIsLoading(true);
    try {
      const response = await apiService.register(userData);
      
      if (!response.requires_verification && response.user && response.token) {
        setUser(response.user);
      }
      
      return { requires_verification: response.requires_verification };
    } finally {
      setIsLoading(false);
    }
  };

  const verifyEmail = async (email: string, otp: string) => {
    setIsLoading(true);
    try {
      const response = await apiService.verifyEmail(email, otp);
      if (response.user && response.token) {
        setUser(response.user);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    apiService.logout();
    setUser(null);
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const isAuthenticated = !!user && !!localStorage.getItem('authToken');

  const value: UserAuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    verifyEmail,
    updateUser,
  };

  return (
    <UserAuthContext.Provider value={value}>
      {children}
    </UserAuthContext.Provider>
  );
};

export const useUserAuth = (): UserAuthContextType => {
  const context = useContext(UserAuthContext);
  if (context === undefined) {
    throw new Error('useUserAuth must be used within a UserAuthProvider');
  }
  return context;
};